// @catalog F.010
/**
 * ForecastToolbar — header sticky 1 fila (rediseño handoff Fase 3.a).
 *
 * Replaces legacy ForecastPageToolbar.tsx. Spec literal de:
 *   - README §1 Toolbar (líneas 98-117) — header row + sticky + tokens.
 *   - prototype/forecast-page.jsx (líneas ~150-205) — render + handlers.
 *   - prototype/styles.css (líneas 84-196) — .fc-toolbar* + .fc-search +
 *     .fc-filter-chip + .topbar-action + .fc-primary-action.
 *
 * Contenido (1 fila flex wrap):
 *   h1 "Forecast de compras" + counts "N productos · M con filtros"
 *   spacer flex:1
 *   .fc-search (input con ícono left + clear X)
 *   .fc-filter-chip (dashed → filled+badge cuando active)
 *   .topbar-action (Exportar)
 *   .fc-primary-action (compound: pestaña horizonte + botón negro Pre-seleccionar)
 *
 * Controles de selección masiva (Sel/Desel todos filtrados + Ocultar deseleccionados)
 * van inline entre search y filter chip para recuperar funcionalidad legacy
 * (decisión usuario 2026-05-22 — chips QuickFilters retirados por baja utilidad).
 *
 * Constraints:
 *   - Solo <Stack>/<Box>; PROHIBIDO <Grid> (handoff regla 4).
 *   - <500 LoC (P7).
 *   - data-accent="teal" se aplica al wrapper raíz de ForecastPage (feature 1).
 */
import {
  Box,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarMonthIcon,
  AutoAwesome as AutoAwesomeIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  VisibilityOff as VisibilityOffIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
} from "@mui/icons-material";

export interface ForecastToolbarProps {
  totalCount: number;
  filteredCount: number;
  hasActiveFilters: boolean;
  search: string;
  onSearch: (next: string) => void;
  sideFiltersOpen: boolean;
  onToggleSideFilters: () => void;
  activeAdvancedCount: number;
  onExport: () => void;
  horizonMonths: number;
  onHorizonChange: (months: number) => void;
  onPreselect: () => void;
  horizonOptions?: number[];
  onSelectAllFiltered: () => void;
  onDeselectAllFiltered: () => void;
  hideDeselected: boolean;
  onToggleHideDeselected: () => void;
  // Item 1: alterna vista entre activos (compras ON) y apagados (compras OFF)
  showInactiveOnly: boolean;
  onToggleShowInactive: () => void;
}

const DEFAULT_HORIZON_OPTIONS = [1, 2, 3, 4, 6, 12];

export function ForecastToolbar(
  props: ForecastToolbarProps,
): React.ReactElement {
  const {
    totalCount,
    filteredCount,
    hasActiveFilters,
    search,
    onSearch,
    sideFiltersOpen,
    onToggleSideFilters,
    activeAdvancedCount,
    onExport,
    horizonMonths,
    onHorizonChange,
    onPreselect,
    horizonOptions = DEFAULT_HORIZON_OPTIONS,
    onSelectAllFiltered,
    onDeselectAllFiltered,
    hideDeselected,
    onToggleHideDeselected,
    showInactiveOnly,
    onToggleShowInactive,
  } = props;

  const filterChipActive = sideFiltersOpen || activeAdvancedCount > 0;

  return (
    <Box
      data-testid="forecast-toolbar"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        bgcolor: "var(--surface)",
        borderBottom: "1px solid var(--border)",
        px: "20px",
        pt: "12px",
        pb: 0,
        flexShrink: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="10px"
        sx={{ flexWrap: "wrap", mb: "10px" }}
      >
        <Box
          component="span"
          sx={{
            fontSize: 12.5,
            color: "var(--ink-3)",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <Box
            component="strong"
            sx={{
              color: "var(--ink-1)",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
            }}
          >
            {totalCount}
          </Box>
          productos
          {hasActiveFilters && (
            <>
              <Box component="span" sx={{ color: "var(--ink-4)" }}>
                ·
              </Box>
              <Box
                component="strong"
                sx={{
                  color: "var(--ink-1)",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                }}
              >
                {filteredCount}
              </Box>
              con filtros
            </>
          )}
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* .fc-search */}
        <Box
          sx={{
            position: "relative",
            flex: "0 1 320px",
            minWidth: 200,
          }}
        >
          <Box
            component="span"
            sx={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--ink-3)",
              display: "inline-flex",
              pointerEvents: "none",
            }}
          >
            <SearchIcon sx={{ fontSize: 14 }} />
          </Box>
          <InputBase
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Buscar por nombre o SKU…"
            inputProps={{
              "aria-label": "Buscar producto",
              "data-testid": "forecast-toolbar-search",
              title:
                "Coma = O (une resultados) · + = Y (exige ambos) · - excluye. Ej: wl imp + navid",
            }}
            sx={{
              width: "100%",
              padding: "7px 30px 7px 32px",
              border: "1px solid var(--border)",
              borderRadius: "7px",
              fontSize: 13,
              color: "var(--ink-1)",
              bgcolor: "var(--bg)",
              fontFamily: "inherit",
              "& input": {
                p: 0,
                font: "inherit",
                color: "inherit",
              },
              "&.Mui-focused": {
                borderColor: "var(--accent)",
                bgcolor: "var(--surface)",
                boxShadow: "0 0 0 3px var(--accent-soft)",
              },
            }}
          />
          {search && (
            <IconButton
              onClick={() => onSearch("")}
              aria-label="Limpiar búsqueda"
              size="small"
              sx={{
                position: "absolute",
                right: 4,
                top: "50%",
                transform: "translateY(-50%)",
                width: 22,
                height: 22,
                borderRadius: "4px",
                color: "var(--ink-3)",
                "&:hover": {
                  bgcolor: "var(--bg-2)",
                  color: "var(--ink-1)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          )}
        </Box>

        {/* Selección masiva: Sel todos / Desel todos / Ocultar deseleccionados */}
        <Stack direction="row" gap="2px" alignItems="center">
          <Tooltip title="Seleccionar todos los filtrados" arrow>
            <IconButton
              size="small"
              onClick={onSelectAllFiltered}
              data-testid="forecast-toolbar-select-all"
              aria-label="Seleccionar todos los filtrados"
              sx={{
                width: 30,
                height: 30,
                borderRadius: "6px",
                color: "var(--ink-3)",
                "&:hover": { bgcolor: "var(--bg-2)", color: "var(--ink-1)" },
              }}
            >
              <SelectAllIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deseleccionar todos los filtrados" arrow>
            <IconButton
              size="small"
              onClick={onDeselectAllFiltered}
              data-testid="forecast-toolbar-deselect-all"
              aria-label="Deseleccionar todos los filtrados"
              sx={{
                width: 30,
                height: 30,
                borderRadius: "6px",
                color: "var(--ink-3)",
                "&:hover": { bgcolor: "var(--bg-2)", color: "var(--ink-1)" },
              }}
            >
              <DeselectIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              hideDeselected
                ? "Mostrar todos (incluir deseleccionados)"
                : "Ocultar deseleccionados"
            }
            arrow
          >
            <IconButton
              size="small"
              onClick={onToggleHideDeselected}
              data-testid="forecast-toolbar-hide-deselected"
              aria-label="Ocultar deseleccionados"
              aria-pressed={hideDeselected}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "6px",
                color: hideDeselected ? "var(--accent)" : "var(--ink-3)",
                bgcolor: hideDeselected ? "var(--accent-soft)" : "transparent",
                "&:hover": {
                  bgcolor: hideDeselected
                    ? "var(--accent-soft)"
                    : "var(--bg-2)",
                  color: hideDeselected ? "var(--accent)" : "var(--ink-1)",
                },
              }}
            >
              <VisibilityOffIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              showInactiveOnly
                ? "Ver activos (compras ON)"
                : "Ver apagados (compras OFF)"
            }
            arrow
          >
            <IconButton
              size="small"
              onClick={onToggleShowInactive}
              data-testid="forecast-toolbar-show-inactive"
              aria-label="Ver productos apagados"
              aria-pressed={showInactiveOnly}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "6px",
                color: showInactiveOnly ? "var(--accent)" : "var(--ink-3)",
                bgcolor: showInactiveOnly ? "var(--accent-soft)" : "transparent",
                "&:hover": {
                  bgcolor: showInactiveOnly
                    ? "var(--accent-soft)"
                    : "var(--bg-2)",
                  color: showInactiveOnly ? "var(--accent)" : "var(--ink-1)",
                },
              }}
            >
              <PowerSettingsNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* .fc-filter-chip */}
        <Box
          component="button"
          type="button"
          onClick={onToggleSideFilters}
          aria-pressed={filterChipActive}
          data-testid="forecast-toolbar-filter-chip"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 10px",
            border: filterChipActive
              ? "1px solid var(--accent)"
              : "1px dashed var(--border-strong)",
            borderRadius: "7px",
            bgcolor: filterChipActive ? "var(--accent-soft)" : "transparent",
            fontSize: 12.5,
            color: filterChipActive ? "var(--accent)" : "var(--ink-3)",
            fontWeight: 500,
            fontFamily: "inherit",
            cursor: "pointer",
            "&:hover": {
              color: filterChipActive ? "var(--accent)" : "var(--ink-1)",
              borderColor: filterChipActive ? "var(--accent)" : "var(--ink-3)",
            },
          }}
        >
          <FilterListIcon sx={{ fontSize: 13 }} />
          Filtros
          {activeAdvancedCount > 0 && (
            <Box
              component="span"
              sx={{
                bgcolor: "var(--accent)",
                color: "#fff",
                fontSize: 10.5,
                fontWeight: 600,
                padding: "1px 6px",
                borderRadius: "10px",
                minWidth: 16,
                textAlign: "center",
              }}
            >
              {activeAdvancedCount}
            </Box>
          )}
        </Box>

        {/* .topbar-action — Exportar */}
        <Box
          component="button"
          type="button"
          onClick={onExport}
          data-testid="forecast-toolbar-export"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 10px",
            border: "1px solid var(--border)",
            borderRadius: "7px",
            bgcolor: "transparent",
            fontSize: 12.5,
            color: "var(--ink-2)",
            fontWeight: 500,
            fontFamily: "inherit",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "var(--bg-2)",
              color: "var(--ink-1)",
              borderColor: "var(--ink-3)",
            },
          }}
        >
          <DownloadIcon sx={{ fontSize: 13 }} />
          Exportar
        </Box>

        {/* .fc-primary-action — compound horizonte + Pre-seleccionar */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "stretch",
            borderRadius: "8px",
            overflow: "hidden",
            marginLeft: "auto",
            boxShadow: "0 1px 2px rgba(13, 18, 32, 0.06)",
          }}
        >
          <Box
            component="span"
            sx={{
              bgcolor: "var(--bg-2)",
              color: "var(--ink-2)",
              padding: "0 12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: 12.5,
              borderRight: "1px solid var(--border)",
              fontWeight: 500,
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 12 }} />
            Horizonte
            <Tooltip title="Horizonte en meses" arrow>
              <Box
                component="select"
                value={horizonMonths}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onHorizonChange(parseInt(e.target.value, 10))
                }
                aria-label="Horizonte de forecast en meses"
                data-testid="forecast-toolbar-horizon"
                sx={{
                  border: 0,
                  bgcolor: "transparent",
                  color: "var(--ink-1)",
                  font: "inherit",
                  fontWeight: 600,
                  padding: 0,
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                {horizonOptions.map((m) => (
                  <option key={m} value={m}>
                    {m} {m === 1 ? "mes" : "meses"}
                  </option>
                ))}
              </Box>
            </Tooltip>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onPreselect}
            data-testid="forecast-toolbar-preselect"
            sx={{
              bgcolor: "var(--ink-1)",
              color: "#fff",
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              border: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": { bgcolor: "#1a2236" },
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 13 }} />
            Pre-seleccionar
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
